/**
 * Accessibility Integration for Visual Feedback System
 * WCAG 2.1 AA compliant with reduced motion, high contrast, and screen reader support
 */

import {
  VisualFeedbackContext,
  VisualFeedbackState,
  AccessibilityConfig,
  ReducedMotionConfig,
} from "./types";

/**
 * Accessibility integration with comprehensive WCAG support
 */
export class AccessibilityIntegration {
  private config: AccessibilityConfig;
  private reducedMotionConfig: ReducedMotionConfig;
  private mediaQueries: Map<string, MediaQueryList> = new Map();
  private screenReaderAnnouncements: Set<string> = new Set();

  // Accessibility state
  private reducedMotionEnabled: boolean = false;
  private highContrastEnabled: boolean = false;
  private keyboardNavigationEnabled: boolean = false;
  private screenReaderActive: boolean = false;
  private isTabbing: boolean = false;

  private getHtmlTarget(event: Event): HTMLElement | null {
    return event.target instanceof HTMLElement ? event.target : null;
  }

  private handleKeyboardNavigationKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Tab") {
      this.isTabbing = true;
      this.keyboardNavigationEnabled = true;
      document.body.classList.add("terra-keyboard-navigation");
      return;
    }

    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }

    const currentElement = this.getHtmlTarget(event);
    if (currentElement?.classList.contains("terra-feedback-element")) {
      this.handleArrowKeyNavigation(event, currentElement);
    }
  };

  private handleKeyboardNavigationMousedown = (): void => {
    if (!this.isTabbing) {
      return;
    }

    this.isTabbing = false;
    document.body.classList.remove("terra-keyboard-navigation");
  };

  private handleFocusIn = (event: FocusEvent): void => {
    const target = this.getHtmlTarget(event);
    if (target?.classList.contains("terra-feedback-element")) {
      this.handleFeedbackElementFocus(target);
    }
  };

  private handleFocus = (event: FocusEvent): void => {
    const target = this.getHtmlTarget(event);
    if (target && this.keyboardNavigationEnabled) {
      target.classList.add("terra-keyboard-focused");
    }
  };

  private handleBlur = (event: FocusEvent): void => {
    const target = this.getHtmlTarget(event);
    target?.classList.remove("terra-keyboard-focused");
  };

  constructor(config: Partial<AccessibilityConfig> = {}) {
    this.config = {
      // WCAG 2.2 Compliance
      wcag22AA: true,
      colorContrastMinimum: 4.5, // WCAG 2.2 AA standard
      colorContrastEnhanced: 7.0, // WCAG 2.2 AAA standard

      // Motion and Animation
      reducedMotionDetection: true,
      reducedMotionSupport: true,
      respectSystemPreferences: true,

      // Screen Reader Support
      screenReaderSupport: true,
      screenReaderAnnouncements: true,
      ariaLabels: new Map(),

      // Keyboard Navigation
      keyboardNavigationSupport: true,
      focusManagement: true,

      // High Contrast Support
      highContrastSupport: true,

      // Agricultural Context Optimizations
      gloveCompatibility: true,
      outdoorVisibility: true,
      weatherResistantMode: false, // Enabled only when needed

      // Touch and Mobile (WCAG 2.2 requirements)
      touchTargetMinimum: 44, // 44px minimum for WCAG 2.2
      touchTargetSpacing: 8, // 8px minimum spacing

      // Visual Indicators
      visualFocusIndicators: true,
      persistentVisualCues: true,
      emergencyHighVisibility: false, // Enabled for critical alerts

      ...config,
    };

    this.reducedMotionConfig = {
      enabled: true,
      respectSystemPreferences: true,
      fallbackAnimations: new Map(),
      staticAlternatives: new Map(),
      minimumDuration: 50,
    };

    this.initializeAccessibility();
  }

  /**
   * Initialize accessibility detection and support
   */
  private initializeAccessibility(): void {
    this.loadAccessibilityStyles();
    this.setupMediaQueryListeners();
    this.detectScreenReader();
    this.setupKeyboardNavigation();
    this.setupFocusManagement();
    this.initializeAriaLabels();
    this.createLiveRegions();
  }

  /**
   * Load accessibility CSS styles
   */
  private loadAccessibilityStyles(): void {
    // Check if styles are already loaded
    if (document.getElementById("terra-accessibility-styles")) {
      return;
    }

    // Create and inject accessibility styles
    const styleSheet = document.createElement("style");
    styleSheet.id = "terra-accessibility-styles";
    styleSheet.textContent = this.getAccessibilityCSS();
    document.head.appendChild(styleSheet);
  }

  /**
   * Get accessibility CSS content
   */
  private getAccessibilityCSS(): string {
    // In a real implementation, this would import the CSS file
    // For now, we'll include essential styles inline
    return `
      .terra-feedback-element {
        position: relative;
        outline: none;
        transition: all 0.2s ease;
      }

      .terra-feedback-element:focus-visible,
      .terra-feedback-element.terra-keyboard-focused {
        outline: 3px solid #005fcc;
        outline-offset: 2px;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.8);
        z-index: 1001;
      }

      .terra-reduced-motion,
      .terra-reduced-motion * {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }

      .terra-high-contrast {
        border-width: 2px !important;
        box-shadow: 0 0 0 1px currentColor !important;
      }

      .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      }

      .terra-feedback-element[aria-invalid="true"] {
        border-color: #dc2626 !important;
        background-color: rgba(220, 38, 38, 0.1);
      }

      @media (prefers-reduced-motion: reduce) {
        .terra-feedback-element {
          animation: none !important;
          transition: none !important;
        }
      }

      @media (prefers-contrast: high) {
        .terra-feedback-element {
          border-width: 3px;
          text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
        }
      }

      @media (forced-colors: active) {
        .terra-feedback-element {
          forced-color-adjust: none;
          border: 2px solid ButtonText;
          background: ButtonFace;
          color: ButtonText;
        }

        .terra-feedback-element:focus {
          outline: 3px solid Highlight;
          outline-offset: 2px;
        }
      }

      /* Agricultural Context Optimizations */
      .terra-glove-compatible {
        min-width: 44px !important;
        min-height: 44px !important;
        padding: 12px !important;
        border-width: 3px !important;
        border-radius: 8px !important;
        font-size: 1.125rem !important;
        font-weight: 600 !important;
      }

      .terra-glove-compatible:hover,
      .terra-glove-compatible:focus {
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        border-width: 4px !important;
      }

      .terra-outdoor-visibility {
        background-color: rgba(255, 255, 255, 0.95) !important;
        color: #000000 !important;
        text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.8);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
        border: 2px solid #000000 !important;
      }

      .terra-weather-resistant {
        background: linear-gradient(135deg, #000000 0%, #333333 100%) !important;
        color: #FFFF00 !important;
        border: 3px solid #FFFF00 !important;
        text-shadow: 0 0 4px #000000;
        box-shadow: 0 0 8px rgba(255, 255, 0, 0.6);
        font-weight: 700 !important;
      }

      .terra-emergency-visibility {
        background: #FF0000 !important;
        color: #FFFFFF !important;
        border: 4px solid #FFFFFF !important;
        animation: emergency-pulse 1s ease-in-out infinite !important;
        font-weight: 700 !important;
        text-shadow: 0 0 2px #000000;
        box-shadow: 0 0 16px rgba(255, 0, 0, 0.8);
      }

      @keyframes emergency-pulse {
        0%, 100% {
          transform: scale(1);
          box-shadow: 0 0 16px rgba(255, 0, 0, 0.8);
        }
        50% {
          transform: scale(1.05);
          box-shadow: 0 0 24px rgba(255, 0, 0, 1.0);
        }
      }

      /* Touch Target Optimization for WCAG 2.2 */
      .terra-touch-optimized {
        min-width: 44px;
        min-height: 44px;
        margin: 4px;
        position: relative;
      }

      .terra-touch-optimized::before {
        content: '';
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        border: 1px dashed transparent;
        pointer-events: none;
      }

      .terra-touch-optimized:focus::before {
        border-color: currentColor;
        opacity: 0.5;
      }

      /* Persistent Visual Cues for Agricultural Equipment */
      .terra-persistent-cue {
        position: relative;
      }

      .terra-persistent-cue::after {
        content: '';
        position: absolute;
        top: -2px;
        right: -2px;
        width: 8px;
        height: 8px;
        background: #00FF00;
        border-radius: 50%;
        animation: persistent-pulse 2s ease-in-out infinite;
      }

      @keyframes persistent-pulse {
        0%, 100% { opacity: 0.7; }
        50% { opacity: 1.0; }
      }
    `;
  }

  /**
   * Create ARIA live regions for announcements
   */
  private createLiveRegions(): void {
    // Polite live region
    if (!document.getElementById("terra-live-polite")) {
      const politeRegion = document.createElement("div");
      politeRegion.id = "terra-live-polite";
      politeRegion.setAttribute("aria-live", "polite");
      politeRegion.setAttribute("aria-atomic", "true");
      politeRegion.className = "sr-only";
      document.body.appendChild(politeRegion);
    }

    // Assertive live region
    if (!document.getElementById("terra-live-assertive")) {
      const assertiveRegion = document.createElement("div");
      assertiveRegion.id = "terra-live-assertive";
      assertiveRegion.setAttribute("aria-live", "assertive");
      assertiveRegion.setAttribute("aria-atomic", "true");
      assertiveRegion.className = "sr-only";
      document.body.appendChild(assertiveRegion);
    }
  }

  /**
   * Setup media query listeners for accessibility preferences
   */
  private setupMediaQueryListeners(): void {
    // Reduced motion detection
    if (this.config.reducedMotionDetection && window.matchMedia) {
      const reducedMotionMQ = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.mediaQueries.set("reducedMotion", reducedMotionMQ);

      this.reducedMotionEnabled = reducedMotionMQ.matches;
      reducedMotionMQ.addEventListener("change", (e) => {
        this.reducedMotionEnabled = e.matches;
        this.onAccessibilitySettingChanged("reducedMotion", e.matches);
      });
    }

    // High contrast detection
    if (this.config.highContrastSupport && window.matchMedia) {
      const highContrastMQ = window.matchMedia("(prefers-contrast: high)");
      this.mediaQueries.set("highContrast", highContrastMQ);

      this.highContrastEnabled = highContrastMQ.matches;
      highContrastMQ.addEventListener("change", (e) => {
        this.highContrastEnabled = e.matches;
        this.onAccessibilitySettingChanged("highContrast", e.matches);
      });
    }

    // Color scheme detection
    if (window.matchMedia) {
      const darkModeMQ = window.matchMedia("(prefers-color-scheme: dark)");
      this.mediaQueries.set("darkMode", darkModeMQ);

      darkModeMQ.addEventListener("change", (e) => {
        this.onAccessibilitySettingChanged(
          "colorScheme",
          e.matches ? "dark" : "light",
        );
      });
    }
  }

  /**
   * Detect screen reader usage
   */
  private detectScreenReader(): void {
    if (!this.config.screenReaderSupport) return;

    // Method 1: Check for screen reader specific properties
    const hasScreenReader =
      "speechSynthesis" in window ||
      navigator.userAgent.includes("JAWS") ||
      navigator.userAgent.includes("NVDA") ||
      navigator.userAgent.includes("ORCA") ||
      navigator.userAgent.includes("VoiceOver");

    // Method 2: Create a test element and check if it's announced
    const testElement = document.createElement("div");
    testElement.setAttribute("aria-live", "polite");
    testElement.setAttribute("aria-atomic", "true");
    testElement.style.cssText = `
      position: absolute;
      left: -10000px;
      width: 1px;
      height: 1px;
      overflow: hidden;
    `;

    document.body.appendChild(testElement);

    // If content is announced within a reasonable time, screen reader is likely active
    testElement.textContent = "Accessibility test";

    setTimeout(() => {
      testElement.remove();
    }, 1000);

    this.screenReaderActive = hasScreenReader;
  }

  /**
   * Setup keyboard navigation support
   */
  private setupKeyboardNavigation(): void {
    if (!this.config.keyboardNavigationSupport) return;

    document.addEventListener("keydown", this.handleKeyboardNavigationKeydown);
    document.addEventListener("mousedown", this.handleKeyboardNavigationMousedown);
  }

  /**
   * Handle arrow key navigation for feedback elements
   */
  private handleArrowKeyNavigation(
    event: KeyboardEvent,
    element: HTMLElement,
  ): void {
    event.preventDefault();

    const feedbackElements = Array.from(
      document.querySelectorAll<HTMLElement>(".terra-feedback-element"),
    );

    const currentIndex = feedbackElements.indexOf(element);
    let nextIndex = currentIndex;

    switch (event.key) {
      case "ArrowUp":
      case "ArrowLeft":
        nextIndex =
          currentIndex > 0 ? currentIndex - 1 : feedbackElements.length - 1;
        break;
      case "ArrowDown":
      case "ArrowRight":
        nextIndex =
          currentIndex < feedbackElements.length - 1 ? currentIndex + 1 : 0;
        break;
    }

    if (nextIndex !== currentIndex && feedbackElements[nextIndex]) {
      feedbackElements[nextIndex].focus();
      this.announceToScreenReader(
        `Focused on ${this.getElementDescription(feedbackElements[nextIndex])}`,
      );
    }
  }

  /**
   * Setup focus management
   */
  private setupFocusManagement(): void {
    if (!this.config.focusManagement) return;

    // Track focus changes for accessibility
    document.addEventListener("focusin", this.handleFocusIn);

    // Ensure focus is visible when using keyboard navigation
    document.addEventListener(
      "focus",
      this.handleFocus,
      true,
    );

    document.addEventListener(
      "blur",
      this.handleBlur,
      true,
    );
  }

  /**
   * Handle focus on feedback elements
   */
  private handleFeedbackElementFocus(element: HTMLElement): void {
    // Announce element description to screen reader
    const description = this.getElementDescription(element);
    this.announceToScreenReader(description);

    // Ensure element is visible (scroll into view if needed)
    element.scrollIntoView({
      behavior: this.reducedMotionEnabled ? "auto" : "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }

  /**
   * Get descriptive text for an element
   */
  private getElementDescription(element: HTMLElement): string {
    // Check for aria-label first
    if (element.hasAttribute("aria-label")) {
      return element.getAttribute("aria-label")!;
    }

    // Check for aria-labelledby
    if (element.hasAttribute("aria-labelledby")) {
      const labelId = element.getAttribute("aria-labelledby")!;
      const labelElement = document.getElementById(labelId);
      if (labelElement) {
        return labelElement.textContent || "";
      }
    }

    // Fallback to element content or role
    const role = element.getAttribute("role");
    const text = element.textContent?.trim();

    if (text) {
      return `${role || "Element"}: ${text}`;
    }

    return role || "Interactive element";
  }

  /**
   * Initialize ARIA labels for common feedback elements
   */
  private initializeAriaLabels(): void {
    const defaultLabels = new Map([
      ["empty-state", "Empty canvas. Drag components here to start building."],
      ["ready-state", "Canvas ready. Drop zone active."],
      ["active-state", "Component being positioned. Use arrow keys to adjust."],
      ["success-state", "Component successfully placed."],
      ["error-state", "Error occurred. Press Enter for recovery options."],
      ["drop-zone", "Drop zone for components."],
      ["alignment-guide", "Alignment guide for positioning."],
      ["error-tooltip", "Error information and recovery options."],
      ["recovery-action", "Recovery action button."],
    ]);

    // Merge with user-provided labels
    defaultLabels.forEach((value, key) => {
      if (!this.config.ariaLabels.has(key)) {
        this.config.ariaLabels.set(key, value);
      }
    });
  }

  /**
   * Apply accessibility optimizations to visual feedback context
   */
  public optimizeForAccessibility(
    context: VisualFeedbackContext,
  ): VisualFeedbackContext {
    const optimizedContext = { ...context };

    // Update user preferences with current accessibility settings
    optimizedContext.userPreferences = {
      ...optimizedContext.userPreferences,
      reducedMotion: this.reducedMotionEnabled,
      highContrast: this.highContrastEnabled,
      touchDevice: optimizedContext.userPreferences.touchDevice,
    };

    return optimizedContext;
  }

  /**
   * Apply reduced motion adaptations
   */
  public applyReducedMotionAdaptations(
    element: HTMLElement,
    state: VisualFeedbackState,
  ): void {
    if (!this.reducedMotionEnabled) return;

    // Remove or simplify animations
    element.style.animationDuration = "0.01ms";
    element.style.animationIterationCount = "1";
    element.style.transitionDuration = `${this.reducedMotionConfig.minimumDuration}ms`;

    // Apply static alternatives
    const staticAlternative =
      this.reducedMotionConfig.staticAlternatives.get(state);
    if (staticAlternative) {
      element.classList.add(staticAlternative);
    }

    // Add reduced motion indicator class
    element.classList.add("terra-reduced-motion");
  }

  /**
   * Apply high contrast adaptations
   */
  public applyHighContrastAdaptations(element: HTMLElement): void {
    if (!this.highContrastEnabled) return;

    element.classList.add("terra-high-contrast");

    // Increase border widths
    const currentBorder = getComputedStyle(element).borderWidth;
    if (currentBorder && currentBorder !== "none") {
      const borderWidth = parseInt(currentBorder) || 1;
      element.style.borderWidth = `${Math.max(borderWidth * 2, 2)}px`;
    }

    // Ensure sufficient color contrast
    this.ensureColorContrast(element);
  }

  /**
   * Apply agricultural context optimizations for glove compatibility
   */
  public applyGloveCompatibility(element: HTMLElement): void {
    if (!this.config.gloveCompatibility) return;

    element.classList.add("terra-glove-compatible", "terra-touch-optimized");

    // Ensure minimum touch target size
    const rect = element.getBoundingClientRect();
    if (rect.width < this.config.touchTargetMinimum) {
      element.style.minWidth = `${this.config.touchTargetMinimum}px`;
    }
    if (rect.height < this.config.touchTargetMinimum) {
      element.style.minHeight = `${this.config.touchTargetMinimum}px`;
    }
  }

  /**
   * Apply outdoor visibility optimizations
   */
  public applyOutdoorVisibility(element: HTMLElement): void {
    if (!this.config.outdoorVisibility) return;

    element.classList.add("terra-outdoor-visibility");

    // Apply weather resistant mode if enabled
    if (this.config.weatherResistantMode) {
      element.classList.add("terra-weather-resistant");
    }
  }

  /**
   * Apply emergency high visibility mode
   */
  public applyEmergencyVisibility(element: HTMLElement): void {
    if (!this.config.emergencyHighVisibility) return;

    element.classList.add("terra-emergency-visibility");
    element.setAttribute("aria-live", "assertive");
    element.setAttribute("role", "alert");
  }

  /**
   * Apply persistent visual cues for agricultural equipment
   */
  public applyPersistentCues(element: HTMLElement): void {
    if (!this.config.persistentVisualCues) return;

    element.classList.add("terra-persistent-cue");
  }

  /**
   * Enable weather resistant mode for outdoor conditions
   */
  public enableWeatherResistantMode(): void {
    this.config.weatherResistantMode = true;
    document.body.classList.add("terra-weather-mode");

    // Apply to all existing elements
    document.querySelectorAll(".terra-feedback-element").forEach((element) => {
      this.applyOutdoorVisibility(element as HTMLElement);
    });

    this.announceToScreenReader(
      "Weather resistant mode activated for enhanced outdoor visibility",
      "assertive",
    );
  }

  /**
   * Enable emergency high visibility mode
   */
  public enableEmergencyMode(): void {
    this.config.emergencyHighVisibility = true;
    document.body.classList.add("terra-emergency-mode");

    // Apply to all existing elements
    document.querySelectorAll(".terra-feedback-element").forEach((element) => {
      this.applyEmergencyVisibility(element as HTMLElement);
    });

    this.announceToScreenReader(
      "Emergency high visibility mode activated",
      "assertive",
    );
  }

  /**
   * Ensure sufficient color contrast for high contrast mode
   */
  private ensureColorContrast(element: HTMLElement): void {
    const computedStyle = getComputedStyle(element);
    const backgroundColor = computedStyle.backgroundColor;
    const color = computedStyle.color;

    // If background is light, use dark text
    if (this.isLightColor(backgroundColor)) {
      element.style.color = "#000000";
      element.style.borderColor = "#000000";
    } else {
      element.style.color = "#FFFFFF";
      element.style.borderColor = "#FFFFFF";
    }
  }

  /**
   * Check if a color is light
   */
  private isLightColor(color: string): boolean {
    // Simple heuristic for color lightness
    const div = document.createElement("div");
    div.style.color = color;
    document.body.appendChild(div);

    const rgbColor = getComputedStyle(div).color;
    document.body.removeChild(div);

    const match = rgbColor.match(/\d+/g);
    if (match && match.length >= 3) {
      const [r, g, b] = match.map(Number);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128;
    }

    return false;
  }

  /**
   * Set ARIA attributes for feedback elements
   */
  public setAriaAttributes(
    element: HTMLElement,
    state: VisualFeedbackState,
    context?: VisualFeedbackContext,
  ): void {
    // Set basic accessibility attributes
    element.setAttribute("role", this.getRoleForState(state));
    element.setAttribute("aria-live", this.getAriaLiveValue(state));
    element.setAttribute("tabindex", "0");

    // Set appropriate aria-label
    const labelKey = this.getLabelKeyForState(state);
    const label = this.config.ariaLabels.get(labelKey);
    if (label) {
      element.setAttribute("aria-label", label);
    }

    // Set state-specific attributes
    switch (state) {
      case VisualFeedbackState.EMPTY:
        element.setAttribute("aria-describedby", "empty-state-description");
        break;
      case VisualFeedbackState.ACTIVE:
        element.setAttribute("aria-busy", "true");
        element.setAttribute("aria-describedby", "active-state-description");
        break;
      case VisualFeedbackState.ERROR:
        element.setAttribute("aria-invalid", "true");
        element.setAttribute("aria-describedby", "error-state-description");
        break;
      case VisualFeedbackState.SUCCESS:
        element.setAttribute("aria-describedby", "success-state-description");
        break;
    }

    // Add CSS class for accessibility
    element.classList.add("terra-feedback-element");
  }

  /**
   * Get appropriate role for feedback state
   */
  private getRoleForState(state: VisualFeedbackState): string {
    switch (state) {
      case VisualFeedbackState.EMPTY:
        return "region";
      case VisualFeedbackState.READY:
        return "application";
      case VisualFeedbackState.ACTIVE:
        return "application";
      case VisualFeedbackState.SUCCESS:
        return "status";
      case VisualFeedbackState.ERROR:
        return "alert";
      default:
        return "region";
    }
  }

  /**
   * Get aria-live value for state
   */
  private getAriaLiveValue(state: VisualFeedbackState): string {
    switch (state) {
      case VisualFeedbackState.ERROR:
        return "assertive";
      case VisualFeedbackState.SUCCESS:
        return "polite";
      default:
        return "polite";
    }
  }

  /**
   * Get label key for state
   */
  private getLabelKeyForState(state: VisualFeedbackState): string {
    return `${state}-state`;
  }

  /**
   * Announce message to screen reader
   */
  public announceToScreenReader(
    message: string,
    priority: "polite" | "assertive" = "polite",
  ): void {
    if (
      !this.config.screenReaderSupport ||
      this.screenReaderAnnouncements.has(message)
    ) {
      return;
    }

    // Use the appropriate live region
    const regionId =
      priority === "assertive" ? "terra-live-assertive" : "terra-live-polite";
    const liveRegion = document.getElementById(regionId);

    if (liveRegion) {
      // Clear previous message and add new one with slight delay
      liveRegion.textContent = "";

      setTimeout(() => {
        liveRegion.textContent = message;
        this.screenReaderAnnouncements.add(message);

        // Clear the message after it's been announced
        setTimeout(() => {
          liveRegion.textContent = "";
          this.screenReaderAnnouncements.delete(message);
        }, 1000);
      }, 100);
    } else {
      // Fallback to creating temporary element if live regions aren't available
      const announcement = document.createElement("div");
      announcement.setAttribute("aria-live", priority);
      announcement.setAttribute("aria-atomic", "true");
      announcement.className = "sr-only";

      document.body.appendChild(announcement);

      setTimeout(() => {
        announcement.textContent = message;
        this.screenReaderAnnouncements.add(message);

        setTimeout(() => {
          announcement.remove();
          this.screenReaderAnnouncements.delete(message);
        }, 1000);
      }, 100);
    }
  }

  /**
   * Handle accessibility setting changes
   */
  private onAccessibilitySettingChanged(setting: string, value: any): void {
    console.log(`Accessibility setting changed: ${setting} = ${value}`);

    // Update relevant visual feedback elements
    const feedbackElements = document.querySelectorAll(
      ".terra-feedback-element",
    );
    feedbackElements.forEach((element) => {
      this.updateElementForAccessibility(
        element as HTMLElement,
        setting,
        value,
      );
    });

    // Announce change to screen reader
    switch (setting) {
      case "reducedMotion":
        this.announceToScreenReader(
          value ? "Reduced motion enabled" : "Reduced motion disabled",
        );
        break;
      case "highContrast":
        this.announceToScreenReader(
          value ? "High contrast mode enabled" : "High contrast mode disabled",
        );
        break;
    }
  }

  /**
   * Update element for accessibility setting change
   */
  private updateElementForAccessibility(
    element: HTMLElement,
    setting: string,
    value: any,
  ): void {
    switch (setting) {
      case "reducedMotion":
        if (value) {
          this.applyReducedMotionAdaptations(
            element,
            VisualFeedbackState.READY,
          );
        } else {
          element.classList.remove("terra-reduced-motion");
          element.style.animationDuration = "";
          element.style.transitionDuration = "";
        }
        break;
      case "highContrast":
        if (value) {
          this.applyHighContrastAdaptations(element);
        } else {
          element.classList.remove("terra-high-contrast");
          element.style.borderWidth = "";
          element.style.color = "";
          element.style.borderColor = "";
        }
        break;
    }
  }

  /**
   * Get current accessibility status
   */
  public getAccessibilityStatus() {
    return {
      reducedMotion: this.reducedMotionEnabled,
      highContrast: this.highContrastEnabled,
      screenReader: this.screenReaderActive,
      keyboardNavigation: this.keyboardNavigationEnabled,
      supportedFeatures: {
        reducedMotionDetection: this.config.reducedMotionDetection,
        highContrastSupport: this.config.highContrastSupport,
        screenReaderSupport: this.config.screenReaderSupport,
        keyboardNavigationSupport: this.config.keyboardNavigationSupport,
        focusManagement: this.config.focusManagement,
      },
    };
  }

  /**
   * Update accessibility configuration
   */
  public updateConfig(newConfig: Partial<AccessibilityConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Dispose of accessibility integration
   */
  public dispose(): void {
    document.removeEventListener("keydown", this.handleKeyboardNavigationKeydown);
    document.removeEventListener("mousedown", this.handleKeyboardNavigationMousedown);
    document.removeEventListener("focusin", this.handleFocusIn);
    document.removeEventListener("focus", this.handleFocus, true);
    document.removeEventListener("blur", this.handleBlur, true);

    // Remove media query listeners
    this.mediaQueries.forEach((mq) => {
      // Remove all listeners (can't remove specific ones without reference)
      // This is a limitation of the MediaQueryList API
    });
    this.mediaQueries.clear();

    // Clear announcements
    this.screenReaderAnnouncements.clear();

    // Remove accessibility classes
    document.body.classList.remove("terra-keyboard-navigation");

    // Remove live regions
    const politeRegion = document.getElementById("terra-live-polite");
    if (politeRegion) {
      politeRegion.remove();
    }

    const assertiveRegion = document.getElementById("terra-live-assertive");
    if (assertiveRegion) {
      assertiveRegion.remove();
    }

    // Remove accessibility styles
    const styleSheet = document.getElementById("terra-accessibility-styles");
    if (styleSheet) {
      styleSheet.remove();
    }
  }
}
